import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';

const slideIn = keyframes`
  from { transform: translateX(-100%); }
  to   { transform: translateX(0); }
`;

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: ${({ theme }) => theme.zIndex.modal};
  background: ${({ theme }) => theme.colors.overlay.scrim};
  transition: opacity 200ms ease-in-out;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Panel = styled.div<{ $open: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 320px;
  max-width: min(90vw, 420px);
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background};

  ${({ $open }) =>
    $open &&
    css`
      animation: ${slideIn} 200ms ease-in-out;
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  ${({ theme }) => theme.device.mobile} {
    /* Оставляем затемнённую зону справа — по ней закрываем drawer */
    width: min(320px, 88vw);
    max-width: min(320px, 88vw);
    padding-bottom: env(safe-area-inset-bottom);
    box-shadow: ${({ theme }) => theme.shadows[2]};
  }
`;

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}

export function Drawer({ open, onClose, children }: DrawerProps) {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      if (open) {
        onClose();
      }
    }
  }, [location.pathname, open, onClose]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  return createPortal(
    <Overlay $open={open} onClick={onClose} aria-hidden={!open}>
      <Panel $open={open} onClick={(e) => e.stopPropagation()}>
        {children}
      </Panel>
    </Overlay>,
    document.body,
  );
}
